import React from 'react';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { CustomEmptyState } from './CustomEmptyState';

interface EmptyStateConfig {
  resource: string;
  title: string;
  subtitle: string;
  actionText: string;
  navigationPath: string;
}

const emptyStateConfigs: Record<string, EmptyStateConfig> = {
  'drivers': {
    resource: 'drivers',
    title: 'No Drivers Yet',
    subtitle: 'Add drivers to manage their profiles, licenses, certifications, and employment history. Build your driver database.',
    actionText: 'Add Driver',
    navigationPath: '/drivers/wizard'
  },
  'users': {
    resource: 'users',
    title: 'No Users Yet',
    subtitle: 'Add team members and manage user accounts. Users can access the system with different roles and permissions.',
    actionText: 'Add User',
    navigationPath: '/users/create'
  },
  'forms': {
    resource: 'forms',
    title: 'No Forms Yet',
    subtitle: 'Create custom forms for data collection, inspections, and documentation. Build your workflow with digital forms.',
    actionText: 'Create Form',
    navigationPath: '/forms/create'
  },
  'completed-forms': {
    resource: 'completed-forms',
    title: 'No Completed Forms',
    subtitle: 'Form submissions and responses will appear here once users start completing forms.',
    actionText: '',
    navigationPath: ''
  },
  'equipment': {
    resource: 'equipment',
    title: 'No Equipment Yet',
    subtitle: 'Track vehicles, trucks, trailers, and other equipment. Manage your fleet inventory and maintenance records.',
    actionText: 'Add Equipment',
    navigationPath: '/equipment/create'
  },
  'driver-employment': {
    resource: 'driver-employment',
    title: 'No Employment History',
    subtitle: 'Employment records will appear here when added to driver profiles. Track work history and references.',
    actionText: 'Add Employment',
    navigationPath: '/driver-employment/create'
  },
  'driver-certifications': {
    resource: 'driver-certifications',
    title: 'No Certifications',
    subtitle: 'Driver certifications and training records will appear here when added to profiles.',
    actionText: 'Add Certification',
    navigationPath: '/driver-certifications/create'
  },
  'equipment-maintenance': {
    resource: 'equipment-maintenance',
    title: 'No Maintenance Records',
    subtitle: 'Maintenance history and service records will appear here. Track repairs, inspections, and scheduled maintenance.',
    actionText: 'Add Maintenance Record',
    navigationPath: '/equipment-maintenance/create'
  },
  'equipment-fuel': {
    resource: 'equipment-fuel',
    title: 'No Fuel Records',
    subtitle: 'Fuel purchases and consumption tracking will appear here. Monitor fuel efficiency and costs.',
    actionText: 'Add Fuel Record',
    navigationPath: '/equipment-fuel/create'
  },
  'equipment-inspections': {
    resource: 'equipment-inspections',
    title: 'No Inspections',
    subtitle: 'Safety inspections and compliance checks will appear here. Stay on top of regulatory requirements.',
    actionText: 'Add Inspection',
    navigationPath: '/equipment-inspections/create'
  },
  'equipment-assignments': {
    resource: 'equipment-assignments',
    title: 'No Assignments',
    subtitle: 'Equipment assignment history will appear here. Track who used which equipment and when.',
    actionText: 'Add Assignment',
    navigationPath: '/equipment-assignments/create'
  },
  'equipment-incidents': {
    resource: 'equipment-incidents',
    title: 'No Incidents',
    subtitle: 'Incident reports and damage records will appear here. Document accidents, breakdowns, and repairs.',
    actionText: 'Add Incident',
    navigationPath: '/equipment-incidents/create'
  }
};

export const createEmptyStateComponent = (resourceKey: string) => {
  return () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    
    const config = emptyStateConfigs[resourceKey];
    if (!config) {
      console.warn(`No empty state configuration found for resource: ${resourceKey}`);
      return null;
    }
    
    return (
      <CustomEmptyState
        resource={config.resource}
        title={translate(`empty_states.${resourceKey}.title`, { _: config.title })}
        subtitle={translate(`empty_states.${resourceKey}.subtitle`, { _: config.subtitle })}
        actionText={config.actionText ? translate(`empty_states.${resourceKey}.action`, { _: config.actionText }) : undefined}
        onAction={config.navigationPath ? () => navigate(config.navigationPath as string) : undefined}
      />
    );
  };
};